import React, { useEffect, useState } from 'react';
import { contentAPI } from '../services/api';
import ContentCard from '../components/ContentCard';
import SearchModal from '../components/SearchModal';
import { Search, Filter } from 'lucide-react';

const Movies = () => {
  const [movies, setMovies] = useState([]);
  const [filteredMovies, setFilteredMovies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [selectedGenre, setSelectedGenre] = useState('all');
  const [genres, setGenres] = useState([]);

  useEffect(() => {
    loadMovies();
  }, []);

  useEffect(() => {
    filterMovies();
  }, [selectedGenre, movies]);

  const loadMovies = async () => {
    try {
      const response = await contentAPI.getAll({ content_type: 'movie', limit: 100 });
      setMovies(response.data);
      
      // Extract unique genres
      const allGenres = new Set();
      response.data.forEach(movie => {
        movie.genre.forEach(g => allGenres.add(g));
      });
      setGenres(Array.from(allGenres).sort());
    } catch (error) {
      console.error('Error loading movies:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterMovies = () => {
    let filtered = [...movies];

    // Genre filter
    if (selectedGenre !== 'all') {
      filtered = filtered.filter(movie => movie.genre.includes(selectedGenre));
    }

    setFilteredMovies(filtered);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-electric/30 border-t-electric rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8" data-testid="movies-page">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-foreground mb-2">Movies</h1>
          <p className="text-muted-foreground">Discover amazing films from around the world</p>
        </div>

        {/* Filters */}
        <div className="mb-8 space-y-4">
          {/* Search Button */}
          <button
            onClick={() => setIsSearchOpen(true)}
            className="w-full flex items-center space-x-3 px-6 py-4 bg-white/5 backdrop-blur-3xl border border-white/20 rounded-xl hover:bg-white/10 hover:border-electric/40 transition-all shadow-xl group"
            style={{ backdropFilter: 'blur(30px) saturate(180%)' }}
            data-testid="open-search-button"
          >
            <Search className="h-5 w-5 text-electric group-hover:scale-110 transition-transform" />
            <span className="text-foreground/60 text-left flex-1">Search movies...</span>
            <kbd className="px-3 py-1 rounded-lg bg-white/10 border border-white/20 text-xs text-foreground/60">⌘K</kbd>
          </button>

          {/* Genre Filter */}
          <div className="flex items-center space-x-3 overflow-x-auto pb-2 scrollbar-hide">
            <Filter className="h-5 w-5 text-muted-foreground flex-shrink-0" />
            <button
              onClick={() => setSelectedGenre('all')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex-shrink-0 ${
                selectedGenre === 'all'
                  ? 'bg-electric text-background'
                  : 'bg-secondary text-foreground hover:bg-secondary/80'
              }`}
              data-testid="genre-filter-all"
            >
              All
            </button>
            {genres.map((genre) => (
              <button
                key={genre}
                onClick={() => setSelectedGenre(genre)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex-shrink-0 ${
                  selectedGenre === genre
                    ? 'bg-electric text-background'
                    : 'bg-secondary text-foreground hover:bg-secondary/80'
                }`}
                data-testid={`genre-filter-${genre.toLowerCase()}`}
              >
                {genre}
              </button>
            ))}
          </div>
        </div>

        {/* Search Modal */}
        <SearchModal
          isOpen={isSearchOpen}
          onClose={() => setIsSearchOpen(false)}
          allContent={movies}
          contentType="movies"
        />

        {/* Results Count */}
        <div className="mb-6">
          <p className="text-muted-foreground">
            Showing {filteredMovies.length} {filteredMovies.length === 1 ? 'movie' : 'movies'}
          </p>
        </div>

        {/* Movies Grid */}
        {filteredMovies.length > 0 ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4" data-testid="movies-grid">
            {filteredMovies.map((movie) => (
              <ContentCard key={movie.id} content={movie} />
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <p className="text-muted-foreground text-lg">No movies found</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Movies;
